import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '../../../contexts/ThemeContext';
import useSmoText from '../smoText';

/** Recharts colours that read well on both themes. */
export const useChartTheme = () => {
  const { isDarkMode } = useTheme();
  return {
    grid: isDarkMode ? 'rgba(255,255,255,0.08)' : '#e2e8f0',
    tick: { fill: isDarkMode ? '#94a3b8' : '#64748b', fontSize: 11 },
    tooltip: {
      contentStyle: {
        background: isDarkMode ? '#1f232b' : '#ffffff',
        border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.12)' : '#e2e8f0'}`,
        borderRadius: 12,
        color: isDarkMode ? '#e2e8f0' : '#1e293b',
        fontSize: 12,
      },
      labelStyle: { color: isDarkMode ? '#f8fafc' : '#0f172a', fontWeight: 600 },
      cursor: { fill: isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(15,23,42,0.04)' },
    },
    legend: { fontSize: 12, color: isDarkMode ? '#cbd5e1' : '#475569' },
  };
};

// Chart data keeps its Turkish keys (same as before); only the shown names change
const NAME_KEYS = {
  Ev: 'home',
  Vizit: 'visits',
  Hastane: 'site',
  Sorun: 'issues',
  'Kaçırılan': 'missed',
  'Ev Viziti': 'homeVisit',
  'Hastane Viziti': 'siteVisit',
  'SLA Karşılanma %': 'slaMet',
  'Sorun Oranı (%)': 'issueRate',
  'SLA Uyumu (%)': 'slaCompliance',
  'Dokümantasyon (%)': 'documentation',
  'Sorunlu Vizit': 'withIssue',
  'Sorunsuz Vizit': 'withoutIssue',
};

/** Translates a Turkish chart series / category name. */
export const useChartName = () => {
  const { label } = useSmoText();
  return useCallback((name) => (NAME_KEYS[name] ? label(`chart.${NAME_KEYS[name]}`) : name), [label]);
};

/** White card around one chart. */
export const ChartCard = ({ title, className = '', children }) => (
  <div className={`tw-min-w-0 tw-rounded-2xl tw-border tw-border-slate-200/80 tw-bg-white/70 tw-p-4 dark:tw-border-white/10 dark:tw-bg-white/[0.03] ${className}`}>
    <h4 className="tw-mb-3 tw-border-b tw-border-slate-200/70 tw-pb-2 tw-text-sm tw-font-semibold tw-text-slate-800 dark:tw-border-white/10 dark:tw-text-slate-100">{title}</h4>
    {children}
  </div>
);

ChartCard.propTypes = {
  title: PropTypes.string.isRequired,
  className: PropTypes.string,
  children: PropTypes.node,
};
